import React, {useEffect, useState} from "react";
import {
    Box,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoActionTypeDetails from "./AutoActionTypeDetails";

function formatDate(value) {
    if (!value) {
        return "—";
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function formatBoolean(value) {
    if (value === true || value === false) {
        return value ? "Yes" : "No";
    }
    return "—";
}

const COLUMNS = [
    {id: "id", label: "ID", width: "72px"},
    {id: "name", label: "Name", minWidth: "140px"},
    {id: "type", label: "Type", minWidth: "280px", width: "280px"},
    {id: "actionType", label: "Action type", width: "120px"},
    {id: "coverage", label: "Coverage", width: "110px"},
    {id: "isActive", label: "Active", width: "80px"},
    {id: "organizationId", label: "Trends org ID", width: "110px"},
    {id: "verticalId", label: "Vertical ID", width: "100px"},
    {id: "dateCreated", label: "Created", width: "180px"},
    {id: "lastUpdated", label: "Updated", width: "180px"},
    {id: "actions", label: "Actions", align: "right", width: "72px"},
];

const COLUMN_COUNT = COLUMNS.length + 1;

const tableCellSx = {
    fontFamily: "LarsseitRegular",
    fontSize: "14px",
    fontWeight: 500,
    color: "#0C2728",
    padding: "8px 5px",
    whiteSpace: "normal",
    "&:first-of-type": {
        paddingLeft: "18px",
    },
    "&:last-of-type": {
        paddingRight: "18px",
    },
};

const tableHeadSx = {
    ...tableCellSx,
    fontFamily: "LarsseitMedium",
};

const ellipsisSx = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

function displayValue(columnId, row) {
    if (columnId === "isActive") {
        return formatBoolean(row.isActive);
    }
    if (columnId === "dateCreated") {
        return formatDate(row.dateCreated);
    }
    if (columnId === "lastUpdated") {
        return formatDate(row.lastUpdated);
    }
    const value = row[columnId];
    return value == null || value === "" ? "—" : value;
}

function AutoActionRow({row, rowKey, isExpanded, onToggle, deletingAutoActionId, onDelete}) {
    const expandedCellSx = isExpanded ? {borderBottom: "none"} : {};

    return (
        <>
            <TableRow>
                <TableCell sx={{...tableCellSx, width: "40px", ...expandedCellSx}}>
                    <IconButton
                        aria-label={isExpanded ? "Hide auto action details" : "Show auto action details"}
                        size="small"
                        onClick={() => onToggle(rowKey)}
                        sx={{padding: 0, color: "#495D5E"}}
                    >
                        {isExpanded ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                        )}
                    </IconButton>
                </TableCell>
                {COLUMNS.map((column) => {
                    if (column.id === "actions") {
                        return (
                            <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                    ...tableCellSx,
                                    maxWidth: column.width,
                                    minWidth: column.minWidth,
                                    ...expandedCellSx,
                                }}
                            >
                                <IconButton
                                    aria-label="Delete auto action"
                                    size="small"
                                    disabled={row.id == null || deletingAutoActionId === row.id}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(row);
                                    }}
                                    sx={{color: "#495D5E"}}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </TableCell>
                        );
                    }
                    return (
                        <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                                ...tableCellSx,
                                maxWidth: column.width,
                                minWidth: column.minWidth,
                                ...expandedCellSx,
                            }}
                        >
                            <Box sx={ellipsisSx}>{displayValue(column.id, row)}</Box>
                        </TableCell>
                    );
                })}
            </TableRow>
            <TableRow>
                <TableCell
                    colSpan={COLUMN_COUNT}
                    sx={{
                        paddingTop: 0,
                        paddingBottom: 0,
                        borderBottom: isExpanded ? "1px solid #EEEFEF" : "none",
                    }}
                >
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                            sx={{
                                my: 1,
                                py: 1.25,
                                px: 1.5,
                                borderRadius: "5px",
                                border: "1px solid #EEEFEF",
                                bgcolor: "#F8F9F9",
                            }}
                        >
                            <AutoActionTypeDetails row={row} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function AutoActionsTable({rows, deletingAutoActionId, onDelete}) {
    const [expandedRows, setExpandedRows] = useState({});

    useEffect(() => {
        setExpandedRows({});
    }, [rows]);

    const toggleRow = (key) => {
        setExpandedRows((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    return (
        <Paper
            sx={{
                width: "100%",
                overflow: "hidden",
                borderRadius: "5px",
                boxShadow: "none",
                border: "1px solid #EEEFEF",
            }}
        >
            <TableContainer sx={{maxHeight: 740}}>
                <Table stickyHeader aria-label="auto actions table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{...tableHeadSx, width: "40px"}} />
                            {COLUMNS.map((column) => (
                                <TableCell
                                    key={column.id}
                                    align={column.align}
                                    sx={{
                                        ...tableHeadSx,
                                        width: column.width,
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => {
                            const key = row.id ?? `row-${index}`;
                            return (
                                <AutoActionRow
                                    key={key}
                                    row={row}
                                    rowKey={key}
                                    isExpanded={!!expandedRows[key]}
                                    onToggle={toggleRow}
                                    deletingAutoActionId={deletingAutoActionId}
                                    onDelete={onDelete}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}
